/**
 * Onglet Profil : altitude (terrain, rail) et vitesse v(x) sur toute la ligne,
 * gares en repères verticaux. On y voit les creux de vitesse de chaque arrêt.
 * SVG en ligne, ~1 300 points, recalculé à chaque changement de scénario.
 */
import { useMemo, useState } from "react";
import { communeAt } from "../model/route";
import { useApp } from "../state/store";
import { COULEURS, aire, cheminXY, echelle, placerVilles, useLargeur } from "./cadre";
import { profilSeries, ticks } from "./series";

const M = { gauche: 44, droite: 14, haut: 36, bas: 22, entre: 18 };
const H_ALT = 118;
const H_VIT = 96;
const HAUTEUR = M.haut + H_ALT + M.entre + H_VIT + M.bas;

export function Profil() {
  const route = useApp((s) => s.route)!;
  const result = useApp((s) => s.result);
  const selected = useApp((s) => s.selected);
  const select = useApp((s) => s.select);
  const setHover = useApp((s) => s.setHover);
  const stationName = useApp((s) => s.stationName);
  const useVmax = useApp((s) => s.scenario.settings.useVmax);
  const [ref, largeur] = useLargeur<HTMLDivElement>();
  const [survol, setSurvol] = useState<number | null>(null);

  const series = useMemo(() => profilSeries(route, result), [route, result]);
  const L = route.length_m / 1000;
  const zMax = useMemo(() => Math.max(...route.z_terrain, ...route.z_rail), [route]);
  const zMin = useMemo(() => Math.min(0, ...route.z_terrain, ...route.z_rail), [route]);

  const x0 = M.gauche;
  const x1 = Math.max(x0 + 40, largeur - M.droite);
  const sx = echelle(0, L, x0, x1);
  const altHaut = M.haut;
  const altBas = altHaut + H_ALT;
  const vitHaut = altBas + M.entre;
  const vitBas = vitHaut + H_VIT;
  const sz = echelle(zMin, zMax * 1.08, altBas, altHaut);
  const vMaxAxe = Math.max(120, route.hypotheses.vmax_line_kmh + 10);
  const sv = echelle(0, vMaxAxe, vitBas, vitHaut);

  const stops = result?.stops ?? [];
  const idxAt = (clientX: number, rect: DOMRect) => {
    const pk = ((clientX - rect.left - x0) / (x1 - x0)) * L;
    return Math.min(route.coords.length - 1, Math.max(0, Math.round((pk * 1000) / route.step_m)));
  };
  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const idx = idxAt(e.clientX, e.currentTarget.getBoundingClientRect());
    setSurvol(idx);
    setHover(idx);
  };
  const onLeave = () => {
    setSurvol(null);
    setHover(null);
  };
  const onClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const idx = idxAt(e.clientX, e.currentTarget.getBoundingClientRect());
    let best: number | null = null;
    for (const s of stops) if (best === null || Math.abs(s - idx) < Math.abs(best - idx)) best = s;
    if (best !== null && Math.abs(best - idx) * route.step_m < 1500) select(best);
  };

  const i = survol;
  const gareProche = i !== null && stops.length ? stops.reduce((a, b) => (Math.abs(b - i) < Math.abs(a - i) ? b : a)) : null;
  const infoX = i !== null ? sx((i * route.step_m) / 1000) : 0;

  return (
    <section className="graphique" aria-label="Profil en long et vitesse">
      <h2>Profil en long et vitesse</h2>
      <ul className="legende" aria-label="Légende">
        <li><span className="cle" style={{ background: COULEURS.scenario }} /> rail</li>
        <li><span className="cle aire" style={{ background: COULEURS.terrain }} /> terrain</li>
        <li><span className="cle" style={{ background: COULEURS.scenario }} /> vitesse</li>
        {useVmax && <li><span className="cle" style={{ background: COULEURS.limite }} /> limite</li>}
      </ul>
      <div ref={ref} className="cadre">
        {largeur > 0 && (
          <svg width={largeur} height={HAUTEUR} role="img" onPointerMove={onMove} onPointerLeave={onLeave} onClick={onClick}>
            <title>Altitude du rail et du terrain, puis vitesse du train, le long du tracé</title>
            {/* grilles et axes */}
            {ticks(0, zMax, 3).map((z) => (
              <g key={"z" + z}>
                <line x1={x0} x2={x1} y1={sz(z)} y2={sz(z)} stroke={COULEURS.grille} />
                <text x={x0 - 6} y={sz(z) + 4} textAnchor="end" className="axe">{z} m</text>
              </g>
            ))}
            {[0, 50, 100].map((v) => (
              <g key={"v" + v}>
                <line x1={x0} x2={x1} y1={sv(v)} y2={sv(v)} stroke={COULEURS.grille} />
                <text x={x0 - 6} y={sv(v) + 4} textAnchor="end" className="axe">{v}</text>
              </g>
            ))}
            <text x={x0 - 6} y={vitHaut - 4} textAnchor="end" className="axe">km/h</text>
            {ticks(0, L, 6).map((k) => (
              <text key={"k" + k} x={sx(k)} y={vitBas + 15} textAnchor="middle" className="axe">{k} km</text>
            ))}
            {/* repères des villes */}
            {placerVilles(route.landmarks.map((l) => ({ name: l.name, x: sx((l.idx * route.step_m) / 1000) })), x0, x1).map((v) => (
              <g key={v.name}>
                <line x1={v.x} x2={v.x} y1={altHaut - 4} y2={vitBas} stroke={COULEURS.grille} />
                <text x={v.x} y={altHaut - 8 - v.dy} textAnchor={v.anchor} className="ville">{v.name}</text>
              </g>
            ))}
            {/* gares */}
            {stops.map((s) => (
              <line
                key={s}
                x1={sx((s * route.step_m) / 1000)}
                x2={sx((s * route.step_m) / 1000)}
                y1={altHaut}
                y2={vitBas}
                stroke={s === selected ? COULEURS.accent : COULEURS.grille}
                strokeWidth={s === selected ? 2.5 : 1}
              />
            ))}
            {/* altitude */}
            <path d={aire(series.pk, series.terrain, sx, sz, zMin)} fill={COULEURS.terrain} fillOpacity={0.18} />
            <path d={cheminXY(series.pk, series.rail, sx, sz)} fill="none" stroke={COULEURS.scenario} strokeWidth={2} strokeLinejoin="round" />
            {/* vitesse */}
            {useVmax && (
              <path d={cheminXY(series.pk, series.vmax, sx, sv)} fill="none" stroke={COULEURS.limite} strokeWidth={1.5} strokeLinejoin="round" />
            )}
            {result && (
              <>
                <path d={aire(series.pk, series.v, sx, sv, 0)} fill={COULEURS.scenario} fillOpacity={0.12} />
                <path d={cheminXY(series.pk, series.v, sx, sv)} fill="none" stroke={COULEURS.scenario} strokeWidth={2} strokeLinejoin="round" />
              </>
            )}
            <line x1={x0} x2={x1} y1={vitBas} y2={vitBas} stroke={COULEURS.grille} />
            {/* curseur */}
            {i !== null && (
              <g pointerEvents="none">
                <line x1={infoX} x2={infoX} y1={altHaut} y2={vitBas} stroke={COULEURS.encre} strokeWidth={1} />
                <circle cx={infoX} cy={sz(route.z_rail[i])} r={4} fill={COULEURS.scenario} stroke="#fff" strokeWidth={2} />
                {result && <circle cx={infoX} cy={sv(result.v[i] * 3.6)} r={4} fill={COULEURS.scenario} stroke="#fff" strokeWidth={2} />}
              </g>
            )}
          </svg>
        )}
        {i !== null && (
          <div className="infobulle" style={largeur < 520 ? { left: x0, right: 8 } : { left: infoX + (infoX > largeur * 0.6 ? -12 : 12), transform: infoX > largeur * 0.6 ? "translateX(-100%)" : undefined }}>
            <p><strong>PK {((i * route.step_m) / 1000).toFixed(1)}</strong> · {communeAt(route, i)}</p>
            <p><strong>{route.z_rail[i].toFixed(0)} m</strong> rail · {route.z_terrain[i].toFixed(0)} m terrain · pente {(route.grade[Math.min(i, route.grade.length - 1)] * 100).toFixed(1).replace("-", "−")} %</p>
            {result && <p><strong>{(result.v[i] * 3.6).toFixed(0)} km/h</strong>{useVmax && ` · limite ${route.vmax_kmh[i]} km/h`}</p>}
            {gareProche !== null && (
              <p>{gareProche === i ? "gare : " : `gare la plus proche : `}{stationName(gareProche)}{gareProche !== i && ` (${((Math.abs(gareProche - i) * route.step_m) / 1000).toFixed(1)} km)`}</p>
            )}
          </div>
        )}
      </div>
      <p className="note">
        Le terrain vient du MNT IGN ; le rail est une reconstitution qui franchit les ravines à niveau (médiane sur 2 km),
        contrainte à {(route.hypotheses.imax * 100).toFixed(1)} % de pente.
        Cliquez sur une gare pour la sélectionner.
      </p>
    </section>
  );
}
